import { authedFetch } from '@/lib/authed-fetch';
import type { ParsedMemberQrJwt } from '@/lib/member-qr';

type ScannedMemberProfile = {
  avatarUrl: string | null;
  bio?: string | null;
  email: string | null;
  fullName: string | null;
  id: string;
  links?: unknown;
  phone?: string | null;
};

type MemberScanResult =
  | {
      error: string;
      status: 'unreadable';
    }
  | {
      member: ScannedMemberProfile;
      status: 'found';
    }
  | {
      error: string;
      status: 'missing';
      userId: string;
    };

type MemberActionResult = {
  message: string;
  status: 'ok';
};

function getErrorMessage(payload: unknown, fallback: string) {
  if (payload && typeof payload === 'object' && 'error' in payload) {
    const { error } = payload as { error?: unknown };
    if (typeof error === 'string') {
      return error;
    }
  }

  return fallback;
}

function displayMemberName(member: ScannedMemberProfile) {
  return member.fullName ?? member.email ?? '6am member';
}

async function resolveMemberScan(scan: ParsedMemberQrJwt): Promise<MemberScanResult> {
  if (!scan.isValid) {
    return {
      error: scan.error,
      status: 'unreadable',
    };
  }

  const response = await authedFetch('/api/member-scan', {
    body: JSON.stringify({ jwt: scan.jwt }),
    headers: {
      'Content-Type': 'application/json',
    },
    method: 'POST',
  });

  const payload = (await response.json()) as MemberScanResult | { error?: string };

  if (!response.ok) {
    return {
      error: getErrorMessage(payload, 'Could not read member QR code.'),
      status: 'unreadable',
    };
  }

  return payload as MemberScanResult;
}

async function addFriend(userId: string): Promise<MemberActionResult> {
  const response = await authedFetch('/api/friends', {
    body: JSON.stringify({ userId }),
    headers: {
      'Content-Type': 'application/json',
    },
    method: 'POST',
  });
  const payload = (await response.json()) as MemberActionResult | { error?: string };

  if (!response.ok) {
    throw new Error(getErrorMessage(payload, 'Could not add friend.'));
  }

  return payload as MemberActionResult;
}

async function addMemberToEvent(eventId: string, userId: string): Promise<MemberActionResult> {
  const response = await authedFetch(`/api/events/${eventId}/rsvps`, {
    body: JSON.stringify({ userId }),
    headers: {
      'Content-Type': 'application/json',
    },
    method: 'POST',
  });
  const payload = (await response.json()) as MemberActionResult | { error?: string };

  if (!response.ok) {
    throw new Error(getErrorMessage(payload, 'Could not add member to event.'));
  }

  return payload as MemberActionResult;
}

export { addFriend, addMemberToEvent, displayMemberName, resolveMemberScan };
export type { MemberActionResult, MemberScanResult, ScannedMemberProfile };
